<script setup lang="ts">
import { computed, useId } from 'vue';
import { modifierPriceAdjustment } from '@shared/lib/modifier-price';

const props = defineProps<{
  group: any;
  selectedIds: Array<string | number>;
  getSelectionCount: (groupId: string | number) => number;
  getGroupMaxSelection: (group: any) => number;
  getGroupMinSelection: (group: any) => number;
  formatSelectionCounter: (current: number, group: any) => string;
  groupAllowsNoSelection: (group: any) => boolean;
  getNoSelectionId: (groupId: string | number) => string;
  hasModifierImage: (modifier: any) => boolean;
  getModifierImageSource: (modifier: any) => string;
}>();

const emit = defineEmits<{
  (e: 'toggle', groupId: string | number, modifierId: string | number, max: number): void;
  (e: 'imageError', event: Event): void;
}>();

const isInvalid = computed(() =>
  props.getGroupMinSelection(props.group) > props.getSelectionCount(props.group.id)
);

const isModifierDisabled = (modifier: any): boolean => {
  if (modifier.is_available === false) return true;

  const max = props.getGroupMaxSelection(props.group);
  if (max <= 1) return false;

  return (
    props.getSelectionCount(props.group.id) >= max &&
    !props.selectedIds.includes(modifier.id)
  );
};

const groupLabelId = `modifier-group-title-${useId()}`;
</script>

<template>
  <div
    class="modifier-group"
    :class="{ 'is-invalid': isInvalid }"
    role="group"
    :aria-labelledby="groupLabelId"
  >
    <div class="modifier-group__header">
      <div>
        <h4 :id="groupLabelId" class="modifier-group__title">
          {{ group.name }}
          <span v-if="group.is_required || Number(group.min_selection ?? group.min_required ?? 0) > 0" class="required-badge">Obligatorio *</span>
        </h4>
        <p v-if="group.description" class="modifier-group__desc">{{ group.description }}</p>
      </div>
      <span class="selection-counter">
        {{ formatSelectionCounter(getSelectionCount(group.id), group) }}
      </span>
    </div>

    <div class="modifiers-list">
      <label
        v-if="groupAllowsNoSelection(group)"
        class="modifier-option modifier-option--none"
        :class="{ 'is-selected': selectedIds.includes(getNoSelectionId(group.id)) }"
        :for="`mod-opt-${group.id}-none`"
      >
        <input
          :id="`mod-opt-${group.id}-none`"
          :type="getGroupMaxSelection(group) === 1 ? 'radio' : 'checkbox'"
          :name="'group-' + group.id"
          :checked="selectedIds.includes(getNoSelectionId(group.id))"
          :aria-label="`No lleve ${group.name.toLowerCase()}`"
          @change="$emit('toggle', group.id, getNoSelectionId(group.id), getGroupMaxSelection(group))"
        />
        <span class="modifier-name">No lleve {{ group.name.toLowerCase() }}</span>
        <span class="modifier-price">Incluido</span>
      </label>
      
      <label 
        v-for="modifier in group.modifiers" 
        :key="modifier.id" 
        class="modifier-option"
        :class="{ 'is-selected': selectedIds.includes(modifier.id) }"
        :for="`mod-opt-${group.id}-${modifier.id}`"
      >
        <input 
          :id="`mod-opt-${group.id}-${modifier.id}`"
          :type="getGroupMaxSelection(group) === 1 ? 'radio' : 'checkbox'" 
          :name="'group-' + group.id"
          :checked="selectedIds.includes(modifier.id)"
          :disabled="isModifierDisabled(modifier)"
:aria-label="modifier.name"
          @change="$emit('toggle', group.id, modifier.id, getGroupMaxSelection(group))"
        />
        <img
          v-if="hasModifierImage(modifier)"
          :src="getModifierImageSource(modifier)"
          :alt="modifier.name"
          class="modifier-thumb"
          loading="lazy"
          @error="$emit('imageError', $event)"
        />
        <span class="modifier-name">{{ modifier.name }}</span>
        <span v-if="modifierPriceAdjustment(modifier) > 0" class="modifier-price">
          +${{ modifierPriceAdjustment(modifier).toFixed(2) }}
        </span>
      </label>
    </div>
  </div>
</template>

<style scoped>
.modifier-group {
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 0.9rem;
  background: #fff;
  min-width: 0;
}

.modifier-group.is-invalid {
  border-color: #fdba74;
  background: #fff7ed;
}

.modifier-group__header {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.8rem;
}

.modifier-group__title {
  margin: 0;
  font-size: 0.98rem;
  font-weight: 800;
  color: #0f172a;
}

.modifier-group__desc {
  margin: 0.25rem 0 0;
  font-size: 0.84rem;
  color: #64748b;
}

.required-badge {
  display: inline-block;
  margin-left: 0.4rem;
  padding: 0.15rem 0.45rem;
  border-radius: 6px;
  background: #fee2e2;
  color: #dc2626;
  font-size: 0.72rem;
  font-weight: 700;
  vertical-align: middle;
}

.selection-counter {
  flex-shrink: 0;
  border-radius: 999px;
  background: #f1f5f9;
  color: #475569;
  font-size: 0.78rem;
  font-weight: 800;
  padding: 0.28rem 0.65rem;
}

.modifiers-list {
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
}

.modifier-option {
  display: grid;
  grid-template-columns: 22px auto 1fr auto;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 0.85rem;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: #fff;
  cursor: pointer;
  transition: border-color 0.15s ease, background-color 0.15s ease;
}

.modifier-option--none {
  grid-template-columns: 22px 1fr auto;
}

.modifier-option:hover {
  border-color: #cbd5e1;
  background: #f8fafc;
}

.modifier-option.is-selected {
  border-color: #e05a2a;
  background: #fff7ed;
}

.modifier-option input {
  width: 18px;
  height: 18px;
  margin: 0;
}

.modifier-thumb {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  object-fit: cover;
  border: 1px solid #e2e8f0;
}

.modifier-name {
  min-width: 0;
  font-size: 0.95rem;
  font-weight: 700;
  color: #1f2937;
}

.modifier-price {
  justify-self: end;
  white-space: nowrap;
  font-size: 0.86rem;
  font-weight: 700;
  color: #64748b;
}

@media (max-width: 768px) {
  .modifier-option {
    grid-template-columns: 22px 40px 1fr auto;
    gap: 0.5rem;
    padding: 0.65rem;
  }
  .modifier-option--none {
    grid-template-columns: 22px 1fr auto;
  }
}
</style>
